import { ChartMetrics, ColumnData, ParsedReport } from './types';
import { CATEGORY_NAMES } from './reportToDashboard';
import { logger } from 'presentation/components/KSExport/reportNTParser/utils';

interface CategorySums {
  startDate: string;
  endDate: string;
  dateRange: string;
  sales: {
    total: number;
    values: Array<{
      value: ColumnData;
      title: string;
      glCode: number | null;
    }>;
  };
  labor: {
    total: number;
    values: Array<{
      value: ColumnData;
      title: string;
      glCode: number | null;
    }>;
  };
  cogs: {
    total: number;
    values: Array<{
      value: ColumnData;
      title: string;
      glCode: number | null;
    }>;
  };
  rent: {
    total: number;
    values: Array<{
      value: ColumnData;
      title: string;
      glCode: number | null;
    }>;
  };
  advertising: { total: number; values: Array<{ value: ColumnData; title: string; glCode: number | null }> };
  gna: {
    total: number;
    values: Array<{
      value: ColumnData;
      title: string;
      glCode: number | null;
    }>;
  };
  controllableExpenses: {
    total: number;
    values: Array<{ value: ColumnData; title: string; glCode: number | null }>;
  };
  nonControllableExpenses: {
    total: number;
    values: Array<{ value: ColumnData; title: string; glCode: number | null }>;
  };
}

export function sumCategories(parsedReport: ParsedReport): { [columnId: string]: CategorySums } {
  const sums: { [columnId: string]: CategorySums } = {};

  //console.log('Parsed Report:', parsedReport);
  // First, get the column IDs sorted by date (most recent first)
  const sortedColumnIds = Object.keys(parsedReport.columns).sort((a, b) => {
    const dateA = new Date(parsedReport.columns[a].end); // Using end date for sorting
    const dateB = new Date(parsedReport.columns[b].end);
    return dateB.getTime() - dateA.getTime(); // Descending order (most recent first)
  });

  // Initialize sums for each column in the sorted order
  sortedColumnIds.forEach(columnId => {
    const column = parsedReport.columns[columnId];
    sums[columnId] = {
      startDate: column.start,
      endDate: column.end,
      dateRange: `${column.start} - ${column.end}`,
      sales: { total: 0, values: [] },
      labor: { total: 0, values: [] },
      cogs: { total: 0, values: [] },
      rent: { total: 0, values: [] },
      advertising: { total: 0, values: [] },
      gna: { total: 0, values: [] },
      controllableExpenses: { total: 0, values: [] },
      nonControllableExpenses: { total: 0, values: [] },
    };
  });

  // Process values maintaining the sorted order
  sortedColumnIds.forEach(columnId => {
    const column = parsedReport.columns[columnId];
    column.values.forEach(value => {
      if (value.data !== null) {
        const rowMetadata = parsedReport.rows[value.rowId];

        // Skip synthetic accounts
        if (rowMetadata.category === 'synthetic') {
          return;
        }

        const valueInfo = {
          value: value.data,
          title: rowMetadata.title,
          glCode: rowMetadata.glCode,
          id: value.rowId,
        };
        //console.log(rowMetadata.category, 'valueInfo', valueInfo,parsedReport.rows[value.rowId]);
        // Add to the appropriate category
        switch (rowMetadata.category) {
          case 'advertising':
            sums[columnId].advertising.total += value.data.amount;
            sums[columnId].advertising.values.push(valueInfo);
            break;
          case 'sales':
            sums[columnId].sales.total += value.data.amount;
            sums[columnId].sales.values.push(valueInfo);
            break;
          case 'labor':
            sums[columnId].labor.total += value.data.amount;
            sums[columnId].labor.values.push(valueInfo);
            break;
          case 'cogs':
            sums[columnId].cogs.total += value.data.amount;
            sums[columnId].cogs.values.push(valueInfo);
            break;
          case 'rent':
            sums[columnId].rent.total += value.data.amount;
            sums[columnId].rent.values.push(valueInfo);
            break;
          case 'gna':
            sums[columnId].gna.total += value.data.amount;
            sums[columnId].gna.values.push(valueInfo);
            break;
          case 'controllableExpenses':
            sums[columnId].controllableExpenses.total += value.data.amount;
            sums[columnId].controllableExpenses.values.push(valueInfo);
            break;
          case 'nonControllableExpenses':
            sums[columnId].nonControllableExpenses.total += value.data.amount;
            sums[columnId].nonControllableExpenses.values.push(valueInfo);
            break;
        }
      }
    });
  });

  // Create a new object with sorted entries
  const sortedSums: { [columnId: string]: CategorySums } = {};
  sortedColumnIds.forEach(columnId => {
    sortedSums[columnId] = sums[columnId];
  });
  return sortedSums;
}

/**
 * Gets the week number for a given date range string.
 * By default, follows ISO 8601 standards (weeks start on Monday).
 *
 * @param dateRangeString String containing date range (format: "YYYY-MM-DD - YYYY-MM-DD")
 * @param startDayOfWeek Optional - Day of week to start counting from (0 = Sunday, 1 = Monday, etc.). Defaults to 1 (Monday) for ISO standard
 * @returns The week number
 */
export const getWeekNumber = (dateRangeString: string, startDayOfWeek: number = 1) => {
  // Extract the start date from the range (first part before the hyphen)
  const startDate = dateRangeString.split(' - ')[0];

  // Create a Date object from the start date
  const date = new Date(startDate);

  // Use UTC to avoid timezone issues
  const targetDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

  // Get day of week (0 = Sunday, 1 = Monday, etc.)
  let dayOfWeek = targetDate.getUTCDay();

  // Adjust day number based on the specified start day of week
  // For example, if startDayOfWeek is 1 (Monday) and the day is 0 (Sunday),
  // we want to treat it as day 6 of the week
  dayOfWeek = (dayOfWeek - startDayOfWeek + 7) % 7;

  // For ISO 8601 (startDayOfWeek = 1), the reference day is Thursday (day 3 when starting from Monday)
  // For US weeks (startDayOfWeek = 0), the reference day would be Wednesday (day 3 when starting from Sunday)
  // So we always want to find the day that's 3 days after the start of the week
  const refDayOffset = 3;

  // Set to the reference day of the week (Thursday for ISO standard)
  targetDate.setUTCDate(targetDate.getUTCDate() - dayOfWeek + refDayOffset);

  // Get first day of the year
  const yearStart = new Date(Date.UTC(targetDate.getUTCFullYear(), 0, 1));

  // Adjust the year start to find the first occurrence of our reference day
  const firstYearDayOfWeek = yearStart.getUTCDay();
  const adjustedDayOfWeek = (firstYearDayOfWeek - startDayOfWeek + 7) % 7;
  const daysToAdd = (7 + refDayOffset - adjustedDayOfWeek) % 7;
  yearStart.setUTCDate(yearStart.getUTCDate() + daysToAdd);

  // If our target date is before the first reference day of the year,
  // we need to use the last reference day of the previous year
  if (targetDate < yearStart) {
    yearStart.setUTCFullYear(yearStart.getUTCFullYear() - 1);
    yearStart.setUTCMonth(11); // December
    yearStart.setUTCDate(31);

    // Find the last reference day of the previous year
    const lastDayOfWeek = yearStart.getUTCDay();
    const adjustedLastDayOfWeek = (lastDayOfWeek - startDayOfWeek + 7) % 7;
    yearStart.setUTCDate(yearStart.getUTCDate() - adjustedLastDayOfWeek + refDayOffset - 7);
  }

  // Calculate week number: number of days since the first reference day, divided by 7, plus 1
  const weekNumber = Math.ceil(((targetDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);

  return weekNumber;
};

export const getChartData = (parsedReport: ParsedReport): ChartMetrics[] => {
  const categorySums = sumCategories(parsedReport);
  const chartFields = { sales: 'Sales', labor: 'Labor', cogs: 'COGS', gna: 'G&A', rent: 'Rent' };
  const chartData: ChartMetrics[] = [];
  Object.entries(categorySums)
    .reverse()
    .forEach(([columnId, columnData]) => {
      // Will be in date order
      const tmpObj: ChartMetrics = {
        weekId: getWeekNumber(columnData.dateRange),
        date: { start: columnData.startDate, end: columnData.endDate },
        data: {},
      };
      Object.entries(chartFields)
        .reverse()
        .forEach(([key, value]) => {
          if (key in columnData) {
            const categoryData = columnData[key as keyof CategorySums];
            if (typeof categoryData === 'object' && 'total' in categoryData) {
              tmpObj.data[value] = {
                value: categoryData.total,
                label: value,
                sales: CATEGORY_NAMES.sales.includes(value.toLowerCase()),
                occupancy: CATEGORY_NAMES.occupancy.includes(value.toLowerCase()),
              };
            }
          }
        });
      chartData.push(tmpObj);
    });
  return chartData;
};
